import {
    type ClientEventParams,
    FACEBOOK_EVENT_MAP,
    type InternalEventName
} from "@hosttools/core/shared/facebookEvents.types";
import {UserContext} from "@hosttools/frontend";
import {useContext, useEffect} from "react";

const FACEBOOK_PIXEL_ID = "2039536889686642";

/**
 * Facebook Pixel Hook - Optimized for Facebook Ads Success
 *
 * This hook encapsulates Facebook Pixel functionality using the original Meta code structure.
 * It's designed to be easily updatable when Meta releases new versions of their pixel code.
 *
 * Features:
 * - Uses original Meta Pixel base code for maximum compatibility
 * - Advanced user matching with external_id for better attribution
 * - Proper deduplication with Conversions API using event IDs
 * - Automatic fbp and fbc parameter collection
 * - Uses Facebook standard events for better optimization
 *
 * To update Facebook Pixel code:
 * 1. Go to https://developers.facebook.com/docs/facebook-pixel/implementation/
 * 2. Copy the latest base code from Meta
 * 3. Replace the initialization code in this file maintaining the same structure
 * 4. Test thoroughly to ensure all events still fire correctly
 *
 * Standard Events tracked:
 * - PageView: All page visits
 * - CompleteRegistration: User registration
 * - Purchase: Subscription purchases
 * - CustomizeProduct: Account connections
 */

// TypeScript declarations for Facebook Pixel
declare global {
    interface Window {
        fbq?: any;
        _fbq?: any;
    }
}

export const useFacebookPixel = () => {
    const {isAuthenticated, user, isLoading} = useContext(UserContext);

    useEffect(() => {
        if (__DEV__ || isLoading) {
            return;
        }

        // Original Facebook Pixel base code (copy-paste from Meta)
        // To update: Go to https://developers.facebook.com/docs/facebook-pixel/implementation/
        // Copy the latest base code and adapt it below maintaining the same structure
        if (!window.fbq) {
            // Initialize fbq function with original Meta structure
            const fbq: any = (...args: any[]) => {
                if (fbq.callMethod) {
                    fbq.callMethod(...args);
                } else {
                    fbq.queue.push(args);
                }
            };

            if (!window._fbq) window._fbq = fbq;
            window.fbq = fbq;
            fbq.push = fbq;
            fbq.loaded = true;
            fbq.version = "2.0";
            fbq.queue = [];

            // Load the script
            const script = document.createElement("script");
            script.async = true;
            script.src = "https://connect.facebook.net/en_US/fbevents.js";
            const firstScript = document.getElementsByTagName("script")[0];
            if (firstScript && firstScript.parentNode) {
                firstScript.parentNode.insertBefore(script, firstScript);
            }
        }

        const pixelId = FACEBOOK_PIXEL_ID;

        // Initialize pixel with advanced matching
        if (!user._id) {
            // Anonymous user
            window.fbq("init", pixelId);
        } else {
            // Authenticated user with external_id for advanced matching
            window.fbq("init", pixelId, {
                external_id: user._id
            });
        }

        // Track PageView (always tracked)
        window.fbq("track", "PageView");
    }, [
        isAuthenticated,
        user._id,
        user.subscriptionStatus,
        user.createdAt,
        user.isBeta,
        isLoading
    ]);
};

/**
 * Type-safe helper function to track internal events (maps to Facebook standard events)
 * Includes proper deduplication support with Conversions API using deterministic event IDs
 */
export const trackFacebookEvent = (
    eventName: InternalEventName,
    parameters: ClientEventParams,
    eventId: string
) => {
    if (__DEV__ || !window.fbq || __IS_BETA__) return;

    const mapping = FACEBOOK_EVENT_MAP[eventName];
    if (!mapping) {
        console.warn(`Unknown internal event name: ${eventName}`);
        return;
    }

    // Merge default parameters with custom parameters
    const allParameters = {
        ...mapping.defaultParams,
        ...parameters
    };

    // Add eventID for deduplication with Conversions API
    const eventData = {...allParameters, eventID: eventId};

    // Track using Facebook standard event
    window.fbq("track", mapping.facebookEventName, eventData);

    // Log for debugging
    if (__DEV__) {
        console.log("Facebook Pixel event tracked:", {
            internalEventName: eventName,
            facebookEventName: mapping.facebookEventName,
            eventId,
            parameters: eventData
        });
    }
};

/**
 * Helper function to track subscription completion with server-provided event ID
 * This ensures client and server use the same event ID for deduplication
 */
export const trackSubscriptionComplete = async (
    userId: string,
    customParameters: ClientEventParams
) => {
    if (__DEV__ || !window.fbq) return;

    try {
        // Call the subscription complete endpoint to get the deterministic event ID
        const response = await fetch("/subscriptionComplete", {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            }
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const {eventId} = data;

        // Track the event using the server-provided event ID
        trackFacebookEvent("subscriptionComplete", customParameters, eventId);

        return data;
    } catch (error) {
        console.error("Failed to track subscription completion:", error);
    }
};
